import React, { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button.jsx'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card.jsx'
import { Badge } from '@/components/ui/badge.jsx'
import { ChefHat, TrendingUp, Users, MapPin, DollarSign, Smartphone, Target, Award, ArrowRight, Mail, Phone, ChevronLeft, ChevronRight } from 'lucide-react'
import InvestorForm from '@/components/InvestorForm.jsx'
import ContactForm from '@/components/ContactForm.jsx'
import SEO from '@/components/SEO.jsx'
import useAsset, { getAssetUrl } from '@/hooks/useAsset'

const InvestorPage = () => {
  const [showInvestorForm, setShowInvestorForm] = useState(false)
  const [showContactForm, setShowContactForm] = useState(false)
  const [currentSlide, setCurrentSlide] = useState(0)

  // Cache-busted assets using hooks
  const truckHollywood = useAsset('/Truck_Hollywood.png')
  const austinHeadshot = useAsset('/austin_headshot.png')
  const beeHeadshot = useAsset('/bee_headshot.png')

  const slideshowImages = [
    { src: getAssetUrl("/img-1.jpg"), alt: "Tailtations Experience", title: "Gourmet Food" },
    { src: getAssetUrl("/img-2.jpg"), alt: "Gourmet Lobster Preparation", title: "Commercial Preparation" },
    { src: getAssetUrl("/img-3.jpg"), alt: "Serving Customers", title: "Exceptional Quality" },
    { src: getAssetUrl("/img-4.jpg"), alt: "Team at Work", title: "Next Level Catering" },
    { src: getAssetUrl("/tailtations_photo_02.jpg"), alt: "Tailtations", title: "Premium Experience" },
    { src: getAssetUrl("/tailtations_photo_03.jpg"), alt: "Tailtations", title: "Fresh Ingredients" },
    { src: getAssetUrl("/tailtations_photo_04.jpg"), alt: "Tailtations", title: "Culinary Excellence" },
    { src: getAssetUrl("/tailtations_photo_05.jpg"), alt: "Tailtations", title: "Quality Service" },
    { src: getAssetUrl("/tailtations_photo_06.jpg"), alt: "Tailtations", title: "Event Catering" },
    { src: getAssetUrl("/tailtations_photo_07.jpg"), alt: "Tailtations", title: "Food Preparation" },
    { src: getAssetUrl("/tailtations_photo_08.jpg"), alt: "Tailtations", title: "Signature Dishes" },
    { src: getAssetUrl("/tailtations_photo_09.jpg"), alt: "Tailtations", title: "Customer Service" },
    { src: getAssetUrl("/tailtations_photo_10.jpg"), alt: "Tailtations", title: "Professional Team" },
    { src: getAssetUrl("/tailtations_photo_11.jpg"), alt: "Tailtations", title: "Food Presentation" },
    { src: getAssetUrl("/tailtations_photo_12.jpg"), alt: "Tailtations", title: "Kitchen Operations" },
    { src: getAssetUrl("/tailtations_photo_13.jpg"), alt: "Tailtations", title: "Menu Selection" },
    { src: getAssetUrl("/tailtations_photo_14.jpg"), alt: "Tailtations", title: "Service Excellence" },
    { src: getAssetUrl("/tailtations_photo_15.jpg"), alt: "Tailtations", title: "Brand Experience" },
    { src: getAssetUrl("/tailtations_photo_16.jpg"), alt: "Tailtations", title: "Venue Setup" },
    { src: getAssetUrl("/tailtations_photo_17.jpg"), alt: "Tailtations", title: "Event Success" },
    { src: getAssetUrl("/tailtations_photo_18.jpg"), alt: "Tailtations", title: "Customer Satisfaction" }
  ]

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slideshowImages.length)
  }

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slideshowImages.length) % slideshowImages.length)
  }

  // Auto-advance slideshow every 2 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slideshowImages.length)
    }, 2000)

    return () => clearInterval(interval)
  }, [slideshowImages.length])

  // Handle hash navigation on mount and hash change
  useEffect(() => {
    const scrollToHash = () => {
      const hash = window.location.hash.slice(1)
      if (hash) {
        setTimeout(() => {
          const element = document.getElementById(hash)
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
        }, 100)
      }
    }

    scrollToHash()
    window.addEventListener('hashchange', scrollToHash)

    return () => window.removeEventListener('hashchange', scrollToHash)
  }, [])

  return (
    <div>
      <SEO
        title="Investor Relations - Tailtations®"
        description="Explore investment opportunities with Tailtations, a rapidly growing gourmet food truck brand. Access financial reports and stakeholder information."
      />
      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto">
          {/* Mobile Layout */}
          <div className="lg:hidden text-center mb-12">
            <Badge className="mb-6 investors_badge">
              🦞 Gourmet Food+Tech Investment Opportunity
            </Badge>
            <h1 className="text-5xl md:text-7xl font-bold text-black mb-6 leading-tight">
              Lux Viral
              <span className="text-primary block">Street Food</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
            Tailtations is redefining the viral food experience with indulgent, luxury deep-fried lobster tails crafted using Michelin-inspired techniques, powered by cutting-edge technology, and scaled through a high-margin, franchise-ready model. With explosive social media traction and a clear path to $13.5M in annual revenue by year two, Tailtations is poised to dominate the premium quick-serve market.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Button
                size="lg"
                className="bg-primary text-white hover:bg-primary/90 hover:text-white text-lg px-8 py-4"
                onClick={() => setShowInvestorForm(true)}
              >
                <DollarSign className="mr-2 h-5 w-5" />
                Invest
              </Button>
            </div>
            <div className="flex justify-center">
              <img
                src={truckHollywood}
                alt="Tailtations Food Truck in Hollywood"
                className="max-w-full h-auto shadow-2xl"
                style={{ borderRadius: '50px' }}
              />
            </div>
          </div>

          {/* Desktop Layout */}
          <div className="hidden lg:grid lg:grid-cols-2 lg:gap-12 lg:items-center">
            <div className="text-left">
              <Badge className="mb-6 investors_badge">
                🦞 Gourmet Food+Tech Investment Opportunity
              </Badge>
              <h1 className="text-5xl xl:text-7xl font-bold text-black mb-6 leading-tight">
                Lux Viral
                <span className="text-primary block">Street Food</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Tailtations is redefining the viral food experience with indulgent, luxury deep-fried lobster tails crafted using Michelin-inspired techniques, powered by cutting-edge technology, and scaled through a high-margin, franchise-ready model. With explosive social media traction and a clear path to $13.5M in annual revenue by year two, Tailtations is poised to dominate the premium quick-serve market.
              </p>
              <div className="flex flex-col xl:flex-row gap-4">
                <Button
                  size="lg"
                  className="bg-primary text-white hover:bg-primary/90 hover:text-white text-lg px-8 py-4"
                  onClick={() => setShowInvestorForm(true)}
                >
                  <DollarSign className="mr-2 h-5 w-5" />
                  Request Investment Package
                </Button>
              </div>
            </div>
            
            <div className="flex justify-center">
              <img
                src={truckHollywood}
                alt="Tailtations Food Truck in Hollywood"
                className="max-w-full h-auto shadow-2xl"
                style={{ borderRadius: '50px' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Key Metrics */}
      <section className="py-16 px-4 investors_section_light">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">At a Glance</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card className="text-center investors_card hover:shadow-lg transition-shadow">
              <CardContent className="py-8 flex flex-col justify-center items-center min-h-[140px]">
                <div className="investors_icon_container mb-4">
                  <DollarSign className="h-8 w-8" />
                </div>
                <div className="text-3xl font-bold text-primary mb-2">$300K</div>
                <div className="text-gray-600">Funding Goal</div>
              </CardContent>
            </Card>
            <Card className="text-center investors_card hover:shadow-lg transition-shadow">
              <CardContent className="py-8 flex flex-col justify-center items-center min-h-[140px]">
                <div className="investors_icon_container mb-4">
                  <TrendingUp className="h-8 w-8" />
                </div>
                <div className="text-3xl font-bold text-primary mb-2">$150K</div>
                <div className="text-gray-600">Monthly Revenue Target</div>
              </CardContent>
            </Card>
            <Card className="text-center investors_card hover:shadow-lg transition-shadow">
              <CardContent className="py-8 flex flex-col justify-center items-center min-h-[140px]">
                <div className="investors_icon_container mb-4">
                  <MapPin className="h-8 w-8" />
                </div>
                <div className="text-3xl font-bold text-primary mb-2">15</div>
                <div className="text-gray-600">Trucks Year 1</div>
              </CardContent>
            </Card>
            <Card className="text-center investors_card hover:shadow-lg transition-shadow">
              <CardContent className="py-8 flex flex-col justify-center items-center min-h-[140px]">
                <div className="investors_icon_container mb-4">
                  <Target className="h-8 w-8" />
                </div>
                <div className="text-3xl font-bold text-primary mb-2">$13.5M</div>
                <div className="text-gray-600">Year 2 Revenue Goal</div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Business Overview */}
      <section id="overview" className="py-20 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Revolutionary Business Model</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Combining premium food, cutting-edge technology, and scalable operations to dominate the gourmet street food market.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">What Makes Us Different</h3>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="investors_icon_container">
                    <ChefHat className="h-6 w-6" />
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold text-gray-900 mb-2">Premium Single Product Focus</h4>
                    <p className="text-gray-600">Deep-fried lobster tails with Michelin-inspired secret seasoning, creating a unique and memorable experience.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="investors_icon_container">
                    <Smartphone className="h-6 w-6" />
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold text-gray-900 mb-2">Technology-Driven Operations</h4>
                    <p className="text-gray-600">Proprietary app platform managing inventory, sales, payments, and logistics across our cloud kitchen network.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="investors_icon_container">
                    <TrendingUp className="h-6 w-6" />
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold text-gray-900 mb-2">Asset-Light Scalability</h4>
                    <p className="text-gray-600">Independent owner-operators reduce corporate overhead while maintaining brand consistency and quality standards.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="investors_icon_container">
                    <Award className="h-6 w-6" />
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold text-gray-900 mb-2">Patent Pending Preservation Process</h4>
                    <p className="text-gray-600">Vacuum sealed, nitrogen flushed packaging provides extended shelf stability, solving for food cost and waste optimizations.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-primary to-primary/80 p-8 rounded-2xl text-white">
              <h3 className="text-2xl font-bold mb-6 text-white">Product Details</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center text-white">
                  <span>1 Tail</span>
                  <span className="font-bold">$20</span>
                </div>
                <div className="flex justify-between items-center text-white">
                  <span>2 Tails</span>
                  <span className="font-bold">$35 ($17.50 each)</span>
                </div>
                <div className="flex justify-between items-center text-white">
                  <span>3 Tails</span>
                  <span className="font-bold">$50 ($16.67 each)</span>
                </div>
                <div className="flex justify-between items-center text-white">
                  <span>4 Tails</span>
                  <span className="font-bold">$60 ($15 each)</span>
                </div>
                <hr className="border-white/20 my-4" />
                <div className="text-sm opacity-90 text-white">
                  <p className="text-white">• Each Tail comes with a free bag of kettle chips (while supplies last)</p>
                  <p className="text-white">• Cooked at 360°F for 6 minutes</p>
                  <p className="text-white">• Instagram-friendly packaging</p>
                  <p className="text-white">• Proprietary Michelin-inspired secret seasoning</p>
                  <p className="text-white">• Premium quality lobster tail sourcing</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Market Opportunity */}
      <section id="opportunity" className="py-20 px-4 investors_section_light">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Massive Market Opportunity</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Targeting high-growth markets with proven demand for premium street food experiences.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="investors_card hover:shadow-xl transition-all border-l-4 border-l-primary">
              <CardHeader className="pb-4">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="investors_icon_container">
                    <MapPin className="h-6 w-6" />
                  </div>
                  <CardTitle className="text-xl">Launch Markets</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-primary/5 rounded-lg border-l-2 border-primary">
                    <span className="font-semibold">Los Angeles, CA</span>
                    <Badge className="bg-primary text-white">Phase 1</Badge>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-primary/5 rounded-lg border-l-2 border-primary">
                    <span className="font-semibold">Miami, FL</span>
                    <Badge className="bg-primary text-white">Phase 1</Badge>
                  </div>
                  <div className="text-sm text-gray-600 mt-4 pt-4 border-t">
                    High foot-traffic areas, nightlife districts, tourist hotspots, and event venues.
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="investors_card hover:shadow-xl transition-all border-l-4 border-l-primary">
              <CardHeader className="pb-4">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="investors_icon_container">
                    <Target className="h-6 w-6" />
                  </div>
                  <CardTitle className="text-xl">Target Customers</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 text-sm list-none">
                  <li className="flex items-start space-x-2">
                    <span className="text-primary font-bold text-lg">•</span>
                    <span>Urban professionals</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-primary font-bold text-lg">•</span>
                    <span>Food enthusiasts & tourists</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-primary font-bold text-lg">•</span>
                    <span>Event attendees</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-primary font-bold text-lg">•</span>
                    <span>Social media savvy consumers</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-primary font-bold text-lg">•</span>
                    <span>Nightlife crowd</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="investors_card hover:shadow-xl transition-all border-l-4 border-l-primary">
              <CardHeader className="pb-4">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="investors_icon_container">
                    <TrendingUp className="h-6 w-6" />
                  </div>
                  <CardTitle className="text-xl">Market Trends</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 text-sm list-none">
                  <li className="flex items-start space-x-2">
                    <span className="text-primary font-bold text-lg">•</span>
                    <span>Growing gourmet street food market</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-primary font-bold text-lg">•</span>
                    <span>Rise of experiential dining</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-primary font-bold text-lg">•</span>
                    <span>Social media food culture</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-primary font-bold text-lg">•</span>
                    <span>Cloud kitchen proliferation</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-primary font-bold text-lg">•</span>
                    <span>Mobile payment adoption</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Photo Slideshow */}
      <section className="py-20 px-4 investors_section_gradient">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-4">Experience Tailtations</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See our gourmet lobster tail experience in action - from fresh preparations to catering.
            </p>
          </div>
          
          <div className="relative max-w-4xl mx-auto">
            <div className="relative overflow-hidden rounded-[50px] shadow-2xl">
              <img 
                src={slideshowImages[currentSlide].src}
                alt={slideshowImages[currentSlide].alt}
                className="w-full h-96 md:h-[500px] object-cover transition-all duration-500"
              />
              
              {/* Navigation Arrows */}
              <button 
                onClick={prevSlide}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all"
                aria-label="Previous image"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              
              <button 
                onClick={nextSlide}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all"
                aria-label="Next image"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
              
              {/* Image Title Overlay */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6">
                <h3 className="text-2xl font-bold" style={{ color: '#ffffff' }}>{slideshowImages[currentSlide].title}</h3>
              </div>
            </div>
            
            {/* Dots Indicator */}
            <div className="flex justify-center mt-6 space-x-2">
              {slideshowImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all ${
                    index === currentSlide 
                      ? 'bg-primary scale-125' 
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Financial Projections */}
      <section id="financials" className="py-20 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Strong Financial Projections</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Conservative estimates show rapid growth and strong unit economics with clear path to profitability.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <Card className="investors_card">
              <CardHeader>
                <CardTitle>Revenue Model</CardTitle>
                <CardDescription>Multiple revenue streams for sustainable growth</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center investors_highlight_box">
                    <span className="font-medium text-white">Per-tail fee from operators</span>
                    <span className="font-bold text-white">$9</span>
                  </div>
                  <div className="space-y-2 text-sm text-gray-600">
                    <p>• Optional operator services (oil changes, cleaning)</p>
                    <p>• Future franchise fees and royalties</p>
                    <p>• Branded advertising partnerships</p>
                    <p>• Merchandise sales</p>
                    <p>• Premium event placements</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="investors_card">
              <CardHeader>
                <CardTitle>Growth Trajectory</CardTitle>
                <CardDescription>Conservative projections based on 300 tails/truck/day</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span>Month 1-6 (2 trucks)</span>
                    <span className="font-bold">$150K/month</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Year 1 (15 trucks)</span>
                    <span className="font-bold">$1.125M/month</span>
                  </div>
                  <div className="flex justify-between items-center investors_highlight_box">
                    <span className="font-medium text-white">Ending Year 2 Target</span>
                    <span className="font-bold text-white">$13.5M annual</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          <div className="mt-12">
            <Card className="investors_card border-2 border-primary/20">
              <CardHeader className="bg-primary py-6 px-6" style={{ backgroundColor: '#962831' }}>
                <CardTitle className="text-center text-2xl mb-2" style={{ color: '#ffffff !important' }}>Investment Breakdown - $300,000</CardTitle>
                <CardDescription className="text-center text-base" style={{ color: '#ffffff !important' }}>Strategic allocation for maximum impact and growth</CardDescription>
              </CardHeader>
              <CardContent className="pt-8">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  <div className="text-center p-4 border-2 border-primary/10 rounded-lg hover:border-primary/30 hover:shadow-md transition-all">
                    <div className="text-3xl font-bold text-primary mb-3">$60K</div>
                    <div className="text-base font-semibold text-gray-900 mb-2">Cloud Kitchen Setup</div>
                    <div className="text-sm text-gray-600">2 cities @ $30K each</div>
                  </div>
                  <div className="text-center p-4 border-2 border-primary/10 rounded-lg hover:border-primary/30 hover:shadow-md transition-all">
                    <div className="text-3xl font-bold text-primary mb-3">$60K</div>
                    <div className="text-base font-semibold text-gray-900 mb-2">Technology Platform</div>
                    <div className="text-sm text-gray-600">App development & AI integration</div>
                  </div>
                  <div className="text-center p-4 border-2 border-primary/10 rounded-lg hover:border-primary/30 hover:shadow-md transition-all">
                    <div className="text-3xl font-bold text-primary mb-3">$80K</div>
                    <div className="text-base font-semibold text-gray-900 mb-2">Initial Trucks</div>
                    <div className="text-sm text-gray-600">2 fully equipped trucks</div>
                  </div>
                  <div className="text-center p-4 border-2 border-primary/10 rounded-lg hover:border-primary/30 hover:shadow-md transition-all">
                    <div className="text-3xl font-bold text-primary mb-3">$50K</div>
                    <div className="text-base font-semibold text-gray-900 mb-2">Branding & Marketing</div>
                    <div className="text-sm text-gray-600">Launch campaigns & materials</div>
                  </div>
                  <div className="text-center p-4 border-2 border-primary/10 rounded-lg hover:border-primary/30 hover:shadow-md transition-all">
                    <div className="text-3xl font-bold text-primary mb-3">$20K</div>
                    <div className="text-base font-semibold text-gray-900 mb-2">Packaging Design</div>
                    <div className="text-sm text-gray-600">Instagram-ready packaging</div>
                  </div>
                  <div className="text-center p-4 border-2 border-primary/10 rounded-lg hover:border-primary/30 hover:shadow-md transition-all">
                    <div className="text-3xl font-bold text-primary mb-3">$30K</div>
                    <div className="text-base font-semibold text-gray-900 mb-2">Initial Inventory</div>
                    <div className="text-sm text-gray-600">Food costs & miscellaneous</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section id="team" className="py-20 px-4 investors_section_light">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Experienced Leadership Team</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Proven track record in technology, food service, and scaling consumer brands.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card className="investors_card hover:shadow-lg transition-shadow">
              <CardHeader className="text-center">
                <div className="w-26 h-26 rounded-full mx-auto mb-4 overflow-hidden" style={{ width: '104px', height: '104px' }}>
                  <img src={austinHeadshot} alt="Austin Hurst" className="w-full h-full object-cover" />
                </div>
                <CardTitle>Austin Hurst</CardTitle>
                <CardDescription>Co-Founder & CEO</CardDescription>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-gray-600">
                  Seasoned operator with extensive experience in deep-tech and Consumer Packaged Goods scaling. 
                  Proven track record of building and scaling technology-driven businesses.
                </p>
                <div className="mt-4">
                  <Badge variant="secondary" className="mr-2">Deep Tech</Badge>
                  <Badge variant="secondary" className="mr-2">CPG</Badge>
                  <Badge variant="secondary">Scaling</Badge>
                </div>
              </CardContent>
            </Card>
            
            <Card className="investors_card hover:shadow-lg transition-shadow">
              <CardHeader className="text-center">
                <div className="w-26 h-26 rounded-full mx-auto mb-4 overflow-hidden" style={{ width: '104px', height: '104px' }}>
                  <img src={beeHeadshot} alt="Chef Bee" className="w-full h-full object-cover" />
                </div>
                <CardTitle>Chef Bee</CardTitle>
                <CardDescription>Co-Founder & Head Chef</CardDescription>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-gray-600">
                  Miami's favorite Omakase chef with deep roots in fried street food culture from Thailand. 
                  Brings authentic culinary expertise and innovative flavor profiles to the brand.
                </p>
                <div className="mt-4">
                  <Badge variant="secondary" className="mr-2">Omakase</Badge>
                  <Badge variant="secondary" className="mr-2">Street Food</Badge>
                  <Badge variant="secondary">Innovation</Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Technology Platform */}
      <section className="py-20 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Proprietary Technology Platform</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Advanced technology stack enabling seamless operations, real-time analytics, and scalable growth.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="investors_card hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Smartphone className="h-5 w-5 text-primary" />
                  <span>Driver App</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600 list-disc list-inside marker:text-primary">
                  <li>Cloud kitchen locator</li>
                  <li>Inventory check-in/out</li>
                  <li>Real-time sales tracking</li>
                  <li>Account balance management</li>
                  <li>Route optimization</li>
                  <li>Communication portal</li>
                </ul>
              </CardContent>
            </Card>
            
            <Card className="investors_card hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <TrendingUp className="h-5 w-5 text-primary" />
                  <span>Analytics Platform</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600 list-disc list-inside marker:text-primary">
                  <li>Sales data aggregation</li>
                  <li>Performance tracking</li>
                  <li>Demand forecasting</li>
                  <li>Operator management</li>
                  <li>Financial reporting</li>
                  <li>Market insights</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="investors_card hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Award className="h-5 w-5 text-primary" />
                  <span>Quality Control</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600 list-disc list-inside marker:text-primary">
                  <li>Temperature monitoring</li>
                  <li>Compliance checklists</li>
                  <li>Photo submissions</li>
                  <li>Brand standard tracking</li>
                  <li>Automated alerts</li>
                  <li>Performance scoring</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section id="contact" className="py-20 px-4 bg-gradient-to-r from-primary to-primary/90 text-white">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6 text-white">Ready to Join the Revolution?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto opacity-90">
            Be part of the next big thing in food service. Invest in Tailtations and help us bring premium lobster street food to America.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-white text-primary hover:bg-white/90 hover:text-primary text-lg px-8 py-4"
              onClick={() => setShowInvestorForm(true)}
            >
              <Mail className="mr-2 h-5 w-5" />
              Request Investment Package
            </Button>
            <Button 
              size="lg" 
              className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-primary text-lg px-8 py-4"
              onClick={() => setShowContactForm(true)}
            >
              <Phone className="mr-2 h-5 w-5" />
              Schedule a Call
            </Button>
          </div>
        </div>
      </section>

      {/* Modal Forms */}
      <InvestorForm 
        isOpen={showInvestorForm} 
        onClose={() => setShowInvestorForm(false)} 
      />
      <ContactForm 
        isOpen={showContactForm} 
        onClose={() => setShowContactForm(false)} 
      />
    </div>
  )
}

export default InvestorPage